import { useState, useRef, useCallback, useEffect } from "react";
import { Exercise } from "../../../types";
import { SwipeDirection } from "../types";
import { ExerciseImage } from "../../../components/exercises/ExerciseImage";

interface ImageWithFallbackProps {
  exercise: Exercise;
}

interface ExerciseCardProps {
  exercise: Exercise;
  stackPosition: number;
  onSwipe: (direction: SwipeDirection) => void;
}

type FlyOutCardElement = HTMLDivElement & {
  flyOut?: (direction: SwipeDirection) => void;
};

const SWIPE_THRESHOLD = 100;

const styles = {
  imageClassName: "fs-card-img",
};

export function IconX() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function IconCheck() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function IconUndo() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 010 11H11" />
    </svg>
  );
}

function ImageWithFallback({ exercise }: ImageWithFallbackProps) {
  const hasImage = Boolean(exercise.image || exercise.image_url);

  if (!hasImage) {
    return (
      <div className="fs-img-placeholder">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(148,163,184,0.6)"
          strokeWidth="1.5"
        >
          <rect x="3" y="3" width="18" height="18" rx="3" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
        <span>{exercise.name}</span>
      </div>
    );
  }

  return (
    <ExerciseImage
      exercise={{ ...exercise, all_view: true }}
      width={360}
      height={320}
      className={styles.imageClassName}
      alt={exercise.name}
    />
  );
}

export default function ExerciseCard({
  exercise,
  stackPosition,
  onSwipe,
}: ExerciseCardProps) {
  const cardRef = useRef<FlyOutCardElement | null>(null);
  const dragState = useRef({
    active: false,
    startX: 0,
    startY: 0,
    currentX: 0,
  });
  const [hintYesOpacity, setHintYesOpacity] = useState(0);
  const [hintNoOpacity, setHintNoOpacity] = useState(0);
  const [overlayYesOpacity, setOverlayYesOpacity] = useState(0);
  const [overlayNoOpacity, setOverlayNoOpacity] = useState(0);
  const [isImageOpen, setIsImageOpen] = useState(false);

  const isTop = stackPosition === 0;
  const hasImage = Boolean(exercise.image || exercise.image_url);

  const flyOut = useCallback(
    (direction: SwipeDirection) => {
      const card = cardRef.current;
      if (!card) return;
      const vw = window.innerWidth;
      const tx = direction === "yes" ? vw * 1.5 : -vw * 1.5;
      const rot = direction === "yes" ? 30 : -30;
      card.style.transition =
        "transform 0.38s cubic-bezier(0.55, 0, 1, 0.45), opacity 0.38s";
      card.style.transform = `translate(${tx}px, -30px) rotate(${rot}deg)`;
      card.style.opacity = "0";
      setTimeout(() => onSwipe(direction), 380);
    },
    [onSwipe],
  );

  const onPointerStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isTop) return;
      if (e.type === "mousedown" && e.button !== 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      dragState.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        currentX: 0,
      };
      const card = cardRef.current;
      if (card) card.style.transition = "none";
    },
    [isTop],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragState.current.active || !isTop) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      dragState.current.currentX = dx;

      const card = cardRef.current;
      if (card) {
        const rot = dx * 0.08;
        card.style.transform = `translate(${dx}px, ${
          dy * 0.3
        }px) rotate(${rot}deg)`;
      }

      const progress = Math.min(Math.abs(dx) / 120, 1);
      if (dx > 0) {
        setHintYesOpacity(progress);
        setHintNoOpacity(0);
        setOverlayYesOpacity(progress * 0.8);
        setOverlayNoOpacity(0);
      } else {
        setHintNoOpacity(progress);
        setHintYesOpacity(0);
        setOverlayNoOpacity(progress * 0.8);
        setOverlayYesOpacity(0);
      }
    },
    [isTop],
  );

  const onPointerEnd = useCallback(() => {
    if (!dragState.current.active || !isTop) return;
    dragState.current.active = false;
    const dx = dragState.current.currentX;

    if (dx > SWIPE_THRESHOLD) {
      flyOut("yes");
    } else if (dx < -SWIPE_THRESHOLD) {
      flyOut("no");
    } else {
      const card = cardRef.current;
      if (card) {
        card.style.transition =
          "transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)";
        card.style.transform = "";
      }
      setHintYesOpacity(0);
      setHintNoOpacity(0);
      setOverlayYesOpacity(0);
      setOverlayNoOpacity(0);
    }
  }, [isTop, flyOut]);

  useEffect(() => {
    if (!isTop) return;
    const card = cardRef.current;
    if (!card) return;
    card.flyOut = flyOut;
  }, [isTop, flyOut]);

  const cardClass = [
    "fs-card",
    isTop ? "is-top" : stackPosition === 1 ? "behind-1" : "behind-2",
  ].join(" ");

  return (
    <div
      ref={cardRef}
      className={cardClass}
      onPointerDown={onPointerStart}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
    >
      <div
        className={`fs-card-image-wrapper${
          hasImage ? " fs-card-image-wrapper-clickable" : ""
        }`}
        onClick={hasImage ? () => setIsImageOpen(true) : undefined}
      >
        <ImageWithFallback exercise={exercise} />
        {hasImage && <div className="fs-gif-badge">GIF</div>}
        <div className="fs-info">
          <div className="fs-name">{exercise.name}</div>
          <div className="fs-tags">
            {[...(exercise.muscle_groups || []), ...(exercise.equipment || [])].map(
              (tag, i) => (
                <span key={i} className="fs-tag">
                  {tag}
                </span>
              ),
            )}
          </div>
        </div>
      </div>

      <div className="fs-hint fs-hint-yes" style={{ opacity: hintYesOpacity }}>
        SIM
      </div>
      <div className="fs-hint fs-hint-no" style={{ opacity: hintNoOpacity }}>
        NÃO
      </div>
      <div
        className="fs-overlay fs-overlay-yes"
        style={{ opacity: overlayYesOpacity }}
      />
      <div
        className="fs-overlay fs-overlay-no"
        style={{ opacity: overlayNoOpacity }}
      />

      {hasImage && isImageOpen && (
        <div
          className="fs-image-modal"
          onClick={() => setIsImageOpen(false)}
        >
          <div className="fs-image-modal-backdrop" />
          <div
            className="fs-image-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="fs-image-modal-close"
              onClick={() => setIsImageOpen(false)}
            >
              <IconX />
            </button>
            <ExerciseImage
              exercise={{ ...exercise, all_view: true }}
              width={720}
              height={720}
              className="fs-image-modal-img"
              alt={exercise.name}
            />
          </div>
        </div>
      )}
    </div>
  );
}

